import type { ReactNode } from "react"
import { useMemo } from "react"

import { useCabinetAccess } from "../../../access/use-access"
import { fetchBranchSettings, fetchCatalog, fetchEmployees } from "../../../api/directory.api"
import { fetchJournal } from "../../../api/journal.api"
import { cx } from "../../../lib/cx"
import { shiftDate, todayInZone } from "../../../lib/format"
import { ProblemScreen } from "../../../shell/states"
import { resolveBranch } from "../../../shell/use-branch"
import { Button, Skeleton } from "../../../ui"
import { useResource } from "../use-resource"
import { OPTIONAL_STEPS, buildChecklist } from "./checklist-model"
import styles from "./onboarding.module.css"

/**
 * 4-А2 «Путь до первой записи».
 *
 * ЭТОТ ЭКРАН НИЧЕГО НЕ ХРАНИТ. Шесть обязательных шагов считаются из справочников
 * при каждом открытии (см. checklist-model.ts): филиал, часы, мастера, услуги, связка
 * «кто что делает» и смены на неделю. Отдельного маршрута чек-листа в API нет, но даже
 * если бы он был, хранимый счётчик рано или поздно разошёлся бы с данными — и владелец
 * увидел бы «готово 6 из 6» на салоне, где ни у кого нет смен.
 *
 * «ВКЛЮЧИТЬ СТРАНИЦУ ЗАПИСИ» ВЫКЛЮЧЕНА, ПОКА НЕ ШЕСТЬ ИЗ ШЕСТИ, и это не строгость ради
 * строгости: включённая запись без смен показывает клиенту пустой календарь, и он уходит
 * к соседям, решив, что салон закрыт.
 */

/** Сколько дней вперёд считаем «неделей» для шага про график. */
const WEEK_AHEAD = 7

export function ChecklistScreen(props: {
  readonly onOpen: (path: string) => void
  readonly onOpenReady: () => void
}): ReactNode {
  const { context } = useCabinetAccess()
  const branch = resolveBranch(context, null)
  const timeZone = branch?.timezone ?? context.tenant.defaultTimezone
  const today = todayInZone(timeZone)

  const branches = useResource((signal) => fetchBranchSettings(signal), "onboarding-branches")
  const catalog = useResource((signal) => fetchCatalog(signal), "onboarding-catalog")
  const employees = useResource((signal) => fetchEmployees(false, signal), "onboarding-employees")

  const journalQuery = useMemo(
    () =>
      branch === null
        ? null
        : {
            branchId: branch.id,
            dateFrom: today,
            dateTo: shiftDate(today, WEEK_AHEAD - 1),
            includeCancelled: false,
          },
    [branch, today],
  )

  const schedule = useResource(
    journalQuery === null ? null : (signal) => fetchJournal(journalQuery, signal),
    journalQuery === null ? null : `${journalQuery.branchId}|${today}`,
  )

  const loading = branches.loading || catalog.loading || employees.loading || schedule.loading

  const view = useMemo(
    () =>
      catalog.data === null
        ? null
        : buildChecklist({
            branches: branches.data ?? [],
            catalog: catalog.data,
            employees: employees.data ?? [],
            workdays: schedule.data?.workdays ?? [],
          }),
    [branches.data, catalog.data, employees.data, schedule.data],
  )

  if (catalog.problem !== null && catalog.data === null) {
    return <ProblemScreen problem={catalog.problem} onRetry={catalog.reload} />
  }

  return (
    <div className={styles.page}>
      <div className={styles.column}>
        <div>
          <h1 className={styles.heading}>Путь до первой записи</h1>
          <p className={styles.progressText}>
            {loading || view === null ? "Смотрим, что уже заполнено…" : view.progressText}
          </p>
        </div>

        <div className={styles.progressTrack}>
          <div
            className={styles.progressFill}
            style={{ width: `${String(view?.progressPercent ?? 0)}%` }}
            role="progressbar"
            aria-valuenow={view?.doneCount ?? 0}
            aria-valuemin={0}
            aria-valuemax={view?.total ?? 6}
            aria-label="Готовность к первой записи"
          />
        </div>

        <div className={styles.groupLabel}>Обязательно — без этого запись не работает</div>

        {view === null
          ? [0, 1, 2, 3, 4, 5].map((row) => (
              <Skeleton key={row} width="100%" height={56} radius={12} />
            ))
          : view.steps.map((step) => (
              <button
                key={step.id}
                type="button"
                className={cx(styles.step, !step.done && styles.stepPending)}
                onClick={() => {
                  if (step.path !== null) props.onOpen(step.path)
                }}
              >
                <span
                  className={cx(styles.stepMark, step.done && styles.stepMarkDone)}
                  aria-hidden="true"
                >
                  {step.done ? "✓" : ""}
                </span>
                <span className={styles.stepBody}>
                  <span className={styles.stepTitle}>{step.title}</span>
                  <span className={styles.stepSub}>{step.detail}</span>
                </span>
                <span className={styles.stepAction}>{step.action}</span>
                <span className="plnr-visually-hidden">
                  {step.done ? "шаг выполнен" : "шаг не выполнен"}
                </span>
              </button>
            ))}

        <div className={styles.groupLabel}>Необязательно — каждый шаг с выгодой</div>

        {OPTIONAL_STEPS.map((step) => (
          <button
            key={step.title}
            type="button"
            className={styles.optionalStep}
            onClick={() => {
              if (step.path !== null) props.onOpen(step.path)
            }}
          >
            <span className={styles.stepBody}>
              <span className={styles.stepTitle}>{step.title}</span>
              <span className={styles.stepSub}>{step.detail}</span>
            </span>
            <span className={styles.stepAction} aria-hidden="true">
              ›
            </span>
          </button>
        ))}
      </div>

      <div className={styles.footer}>
        <Button
          variant="primary"
          deniedReason={
            view === null || view.canPublish
              ? undefined
              : `Пока не хватает: ${view.missingText}. Включённая запись без этого покажет клиенту пустой календарь.`
          }
          onClick={props.onOpenReady}
        >
          Включить страницу записи
        </Button>
        {view === null || view.canPublish ? null : (
          <div className={styles.missing}>Не хватает: {view.missingText}</div>
        )}
      </div>
    </div>
  )
}
